import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

// Roots we treat as tenant portals. Additional roots can be added here as we
// bring on more brands (e.g. "kampus365.com").
const PORTAL_ROOTS = ["edukampus.com", "kampus365.com"];
const RESERVED = new Set([
  "www", "api", "admin", "mail", "blog", "docs", "status", "app",
  "support", "help", "auth", "cdn", "static", "public", "portal",
  "assets", "images", "img", "js", "css", "media", "root", "system",
]);

// Path-based routing prefix — e.g. edukampus.com/p/stpeters-kengeri
// This is the *scalable* entry point (no per-tenant SSL cert needed) and works
// in parallel with subdomain-based routing for flagship campuses that have
// been linked individually in Emergent's deployment console.
const PATH_PREFIX = "/p/";
const SLUG_RE = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

/** Parse the tenant portal slug from the *hostname* (subdomain mode).
 *  Returns null on the apex, on `www`, on reserved subs, on preview host,
 *  and on localhost.
 */
function slugFromHost() {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  for (const root of PORTAL_ROOTS) {
    if (host === root) return null;
    if (host.endsWith("." + root)) {
      const sub = host.slice(0, -(root.length + 1));
      if (sub.includes(".")) return null;
      if (RESERVED.has(sub)) return null;
      return sub;
    }
  }
  return null;
}

/** Parse the tenant portal slug from the *URL path* (path-based mode).
 *  Matches `/p/{slug}` and `/p/{slug}/...anything`.
 *  Returns null if the path doesn't start with the prefix or the segment
 *  isn't a well-formed slug.
 */
function slugFromPath() {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname || "/";
  if (!path.startsWith(PATH_PREFIX)) return null;
  const rest = path.slice(PATH_PREFIX.length);
  const segment = rest.split("/")[0].toLowerCase();
  if (!segment || !SLUG_RE.test(segment)) return null;
  return segment;
}

/** Return the current portal slug from either mode. Subdomain wins if both
 *  are present (rare, but possible if a flagship subdomain also carries a
 *  `/p/other-slug` path — the host is the stronger signal).
 */
export function currentPortalSlug() {
  return slugFromHost() || slugFromPath();
}

/** React-query hook that returns { data, isLoading, isError, slug } for the
 *  current portal metadata. When we're not on a tenant portal, `slug` is null
 *  and the query never fires — callers can render the generic marketing screen.
 */
export function usePortalContext() {
  const slug = currentPortalSlug();
  const q = useQuery({
    queryKey: ["public-portal", slug],
    queryFn: () => api.get(`/public/portal/${slug}`).then((r) => r.data),
    enabled: !!slug,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return { ...q, slug };
}
